import { createClient, AuthResponse } from '@supabase/supabase-js'
import { callAdminApi } from './apiService'
import { RoleType } from '../shared/constants'
import type { SecurityInterests } from '../shared/securityInterests'
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL!
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY!

export type UserThemePreference = 'light' | 'dark' | 'default';

export type TrussUsers = {
  id: string;
  api_key: string | null;
  api_key_id: string | null;
  username: string;
  role: RoleType;
  filters: any[];  // Consider typing these more specifically
  charts: any[];
  liked_authors: string[];
  liked_products: string[];
  liked_sources: string[];
  subscriptions: any[];
  bio?: string;
  avatar_url?: string;
  organization?: string;
  security_interests?: SecurityInterests;
  setTheme?: UserThemePreference;
  firstTimeLogin?: boolean;
  updated_at?: string;
}

export type AccessCodes = {
  id: string;
  api_key: string | null;
  claimed: boolean;
  claimed_at: Date | null;
  email: string | null;
}

export type TrussApiKeys = {
  id: string;
  user_id: string;
  api_key_id: string;
  api_key: string;
  access_type: 'free' | 'premium';
  created_at: string;
}

export type TrussSubscription = {
  id: string;
  user_id: string;
  current_period_end: string;
  status: string;
}

export type ApiKeyUsage = {
  used: number;
  limit: number;
  periodStart: string;
  periodEnd: string;
}

export const supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        // Implicit flow: recovery links work when opened from email (no PKCE verifier in storage).
        flowType: 'implicit',
    },
    db: {
        schema: 'public'
    }
})

export const signInWithOAuth = async (provider: 'google') => {
    const { error } = await supabaseClient.auth.signInWithOAuth({
        provider,
        options: {
            redirectTo: `${window.location.origin}/login`,
        },
    });

    if (error) throw error;
};

// All users start as community users. Key create + disable runs server-side so the
// raw key never appears in browser network responses before it is disabled.
export const signUpUser = async (email: string, password: string) => {
    try {
        const authResponse: AuthResponse = await supabaseClient.auth.signUp({
            email,
            password,
            options: {
                emailRedirectTo: `${window.location.origin}/login`,
            }
        });

        if (authResponse.error) {
            if (authResponse.error.message.includes('Email already registered')) {
                return {
                    user: null,
                    message: 'Email already registered. Please check your email for the confirmation link.'
                };
            }
            throw authResponse.error;
        }
        if (!authResponse.data.user) throw new Error('User creation failed - no user data returned');

        if (!authResponse.data.session) {
            // Confirm-email projects: session is absent until the user verifies.
            // Provisioning runs on first authenticated session (see ensureCommunityUserProvisioned).
            return {
                user: authResponse.data.user,
                message: 'Check your email for the confirmation link!',
            };
        }

        await ensureCommunityUserProvisioned();

        // Do not return api key material to the client.
        return { user: authResponse.data.user };

    } catch (error) {
        console.error('Signup error details:', error);
        if (typeof error === 'object' && error !== null && 'code' in error && error.code === '23505') {
            throw new Error('This email is already registered but not confirmed. Please check your email for the confirmation link.');
        }
        if (error instanceof Error) {
            throw error;
        }
        if (typeof error === 'object' && error !== null && 'message' in error && typeof (error as { message: unknown }).message === 'string') {
            throw new Error((error as { message: string }).message);
        }
        throw new Error(`An unexpected error occurred during signup. Please try again.`);
    }
};

/** Server-side create + disable; response never includes the raw API key. */
export const ensureCommunityUserProvisioned = async () => {
    const { data: provisionData, error: provisionError } = await supabaseClient.functions.invoke(
        'provision-community-user',
        { method: 'POST' },
    );

    if (provisionError) {
        throw new Error(provisionError.message || 'Failed to provision community account');
    }
    if (provisionData && typeof provisionData === 'object' && 'error' in provisionData) {
        throw new Error(String((provisionData as { error: unknown }).error));
    }
};

const redactCommunityApiKey = <T extends { role?: string; api_key?: string | null }>(userData: T): T => {
    if (userData.role === 'community') {
        return { ...userData, api_key: null };
    }
    return userData;
};

export const fetchUserDataFromSupabase = async () => {
    const { data: { user } } = await supabaseClient.auth.getUser();
    if (!user) throw new Error('No user found');

    let { data: userData, error: userError } = await supabaseClient
        .from('truss_users')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

    // Email-confirm signups provision on first authenticated session.
    if (!userError && !userData) {
        await ensureCommunityUserProvisioned();
        const retry = await supabaseClient
            .from('truss_users')
            .select('*')
            .eq('id', user.id)
            .single();
        userData = retry.data;
        userError = retry.error;
    }

    if (userError) {
        console.error('Error fetching user:', userError);
        throw userError;
    }

    if (!userData) {
        throw new Error('User data not found');
    }

    // Community keys stay disabled until upgrade; never expose the secret in the client.
    return redactCommunityApiKey(userData);
};

export const updateUserDataInSupabase = async (updates: {
    username?: string;
    bio?: string;
    avatarUrl?: string;
    organization?: string;
    securityInterests?: SecurityInterests;
    likedAuthors?: string[];
    likedSources?: string[];
}) => {
    const { data: { user }, error: userError } = await supabaseClient.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('No user found');

    const supabaseUpdates: Record<string, unknown> = {
        username: updates.username,
        bio: updates.bio,
        avatar_url: updates.avatarUrl,
        organization: updates.organization,
        security_interests: updates.securityInterests,
        liked_authors: updates.likedAuthors,
        liked_sources: updates.likedSources,
        updated_at: new Date().toISOString(),
    };

    const filteredUpdates = Object.fromEntries(
        Object.entries(supabaseUpdates).filter(([_, value]) => value !== undefined)
    );

    const { error: supabaseError, data } = await supabaseClient
        .from('truss_users')
        .update(filteredUpdates)
        .eq('id', user.id)
        .select()
        .single();

    if (supabaseError) {
        console.error('Update error:', supabaseError);
        throw supabaseError;
    }

    return data;
};

export const fetchApiKeyUsage = async (
    apiKeyId: string,
    role: RoleType
): Promise<ApiKeyUsage> => {
    return callAdminApi<ApiKeyUsage>('/api-key/usage', 'POST', { apiKeyId, role });
};

export const completeFirstTimeLoginInSupabase = async () => {
    const { data: { user }, error: userError } = await supabaseClient.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('No user found');

    const { error } = await supabaseClient
        .from('truss_users')
        .update({
            firstTimeLogin: false,
            updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

    if (error) throw error;
};

export const updateUserThemeInSupabase = async (setTheme: UserThemePreference) => {
    const { data: { user }, error: userError } = await supabaseClient.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('No user found');

    const { error } = await supabaseClient
        .from('truss_users')
        .update({
            setTheme,
            updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

    if (error) throw error;
};

/**
 * Confirm the current password without replacing the app session.
 * Calling signInWithPassword on the shared client drops MFA users to AAL1 and
 * interrupts password updates (AuthenticatedMfaGate remounts the app).
 */
export const verifyCurrentPassword = async (email: string, currentPassword: string) => {
    const verifyClient = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
            persistSession: false,
            autoRefreshToken: false,
            detectSessionInUrl: false,
            storage: {
                getItem: () => null,
                setItem: () => {},
                removeItem: () => {},
            },
        },
        db: {
            schema: 'public',
        },
    });

    const { error } = await verifyClient.auth.signInWithPassword({
        email,
        password: currentPassword,
    });

    if (error) {
        throw new Error('Current password is incorrect');
    }
};

export const updateUserPassword = async (newPassword: string) => {
    const { data, error } = await supabaseClient.auth.updateUser({
        password: newPassword
    });

    if (error) throw error;
    if (!data.user) throw new Error('Password update did not return an updated user');
};

export const sendPasswordResetEmail = async (email: string) => {
    const { error } = await supabaseClient.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) throw error;
};

export const rotateApiKey = async (apiKeyId: string) => {
    const { data: { user } } = await supabaseClient.auth.getUser();
    if (!user) throw new Error('No user found');

    const { newApiKey } = await callAdminApi<{ newApiKey: string }>(
        '/api-key/rotate',
        'POST',
        { apiKeyId }
    );

    // Update the API key in Supabase
    const { error } = await supabaseClient
        .from('api_keys')
        .update({ api_key: newApiKey })
        .eq('api_key_id', apiKeyId)
        .eq('user_id', user.id);

    if (error) throw error;

    return newApiKey;
};

export const fetchUserSubscription = async (): Promise<TrussSubscription | null> => {
    const { data: { user } } = await supabaseClient.auth.getUser();
    if (!user) throw new Error('No user found');

    const { data: subscription, error } = await supabaseClient
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .single();

    if (error) {
        if (error.code === 'PGRST116') { // No rows returned
            return null;
        }
        throw error;
    }

    return subscription;
}; 